import type { AssociatedStatus } from '@/entities/optimization'
import type { FilterRuleModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { OptimizationGroupTableRow } from '../model/types'

import React, { Fragment, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { cva } from 'class-variance-authority'
import { uniqBy } from 'lodash/fp'
import { ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useBulkOptimizationMutation } from '@/entities/optimization'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { clarify } from '@/shared/components/ui/clarify'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import { Tag } from '@/shared/components/ui/tag'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { useAssortmentTableConfigStore } from '@/shared/lib/table-config'
import { getFilterValueByName } from '@/shared/tools/filtering'
import { errorMessage } from '@/shared/tools/message'

import { groupByStatus } from '../lib/groupByStatus'
import { useOptimizationGroupsContext } from './OptimizationGroupsProvider'

export function OptimizationGroupsStatusesManager() {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [{ filters }, setQuery] = useLocationQuery()
	const { showModal } = useModals()
	const tableConfig = useAssortmentTableConfigStore()
	const {
		data,
		dataList,
		rowSelection,
		setRowSelection,
		selectionCount,
		updatingCount,
	} = useOptimizationGroupsContext()
	const isSelected = (id: string) => rowSelection[id]

	const bulkAction = useBulkOptimizationMutation({
		async onSuccess() {
			queryClient.invalidateQueries({
				queryKey: ['pricing-alerts'],
			})

			return queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
		},
	})
	const handleSetExpandedStatus = (status?: string) => {
		trackMassRepricingFilter(status)
		status
			? setQuery({
					filters: uniqBy('name', [
						{ name: 'ui_status', operation: Operators.IN, value: [status] },
						...filters.filter((f: FilterRuleModel) => f.name !== 'schedule'),
					]),
				})
			: setQuery({ filters: null })
	}
	const handleOptimizationBulkAction = async (
		items: OptimizationGroupTableRow[],
		action: OptimizationAction
	) => {
		try {
			if (
				action === OptimizationAction.Lock &&
				hasExportedOptimization(items)
			) {
				const answer = await askToApplyOptimization(t)
				if (!answer) return
			}
			if (action === OptimizationAction.Unlock) {
				const answer = await askToRemoveExportedPrices(t)
				if (!answer || answer === 'close') return
				if (answer === 'revert_full_state') {
					await bulkAction.mutateAsync(
						items.map(({ group }) => ({
							optimization_group_id: group.id,
							action: OptimizationAction.DeleteExport,
						}))
					)
				}
			}
			setRowSelection({})
			expandedStatus &&
				trackAnalyticEvent(expandedStatus.join(','), selectionCount)
			await bulkAction.mutateAsync(
				items.map(({ group, optimization }) => ({
					optimization_group_id: group.id,
					optimization_id: optimization.id,
					action,
				}))
			)
		} catch (err) {
			toast.error(errorMessage(t), { duration: 3000 })
		}
	}
	const handleOpenExportModal = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		showModal('EXPORT_ASSORTMENT', {
			defaultName: 'Multi OG Export',
			requireCallback: true,
			tableConfig,
			context: {
				filters: [
					{
						name: 'optimization_group_id',
						operation: Operators.IN,
						value: dataList
							.filter(
								({ optimization, group }) =>
									optimization.status === OptimizationStatus.Locked &&
									(selectionCount > 0 ? isSelected(group.id) : true)
							)
							.map(getOptGroupId),
					},
				],
			},
		})
	}
	const actionsInProgress = useMemo(
		() => new Set(bulkAction.variables?.map((v) => v.action) || []),
		[bulkAction.variables]
	)

	const handleShowOnlyUpdating = () =>
		setQuery({
			filters: uniqBy('name', [
				{
					name: 'ui_status',
					operation: Operators.IN,
					value: [OptimizationStatus.Updating],
				},
				...filters.filter((f: FilterRuleModel) => f.name !== 'schedule'),
			]),
		})
	const expandedStatus = getFilterValueByName(filters, 'ui_status') as
		string[] | undefined
	const filteredBySchedule = getFilterValueByName(filters, 'schedule.id') as
		string[] | undefined
	const totalCount = data.length || 0
	const optimizationsByStatus = groupByStatus(data)
	const isAllButtonsDisabled = updatingCount > 0 && selectionCount == 0
	const getActions = (
		type: AssociatedStatus,
		items: OptimizationGroupTableRow[]
	) => {
		const isDisabled =
			bulkAction.isPending ||
			isAllButtonsDisabled ||
			!canMakeActionOnList(type, items)

		const postfix =
			!bulkAction.isPending &&
			(selectionCount > 0 ? `(${items.length})` : t('general_all'))

		switch (type) {
			case OptimizationStatus.New:
				return [
					<Button
						key='start'
						data-testid='bulk-start'
						variant='primary'
						size='sm'
						disabled={isDisabled}
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Start)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Start) && <Spinner />}
						{t('optimization_groups_status_btn_start')} {postfix}
					</Button>,
				]
			case OptimizationStatus.Pending:
				return [
					<Button
						key='start'
						data-testid='bulk-start'
						variant='primary'
						size='sm'
						disabled={isDisabled}
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Update)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Update) && <Spinner />}
						{t('app.update')} {postfix}
					</Button>,
				]
			case OptimizationStatus.Scheduled:
				return [
					<Button
						key='stop'
						size='sm'
						variant='warning'
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Stop)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Stop) && <Spinner />}
						{t('optimization_groups_status_btn_stop')} {postfix}
					</Button>,
				]
			case OptimizationStatus.Failed:
				return [
					<Button
						key='restart'
						size='sm'
						disabled={isDisabled}
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Start)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Start) && <Spinner />}
						{t('optimization_groups_status_btn_restart')} {postfix}
					</Button>,
				]
			case OptimizationStatus.Finished:
				return [
					<Button
						key='apply'
						size='sm'
						variant='primary'
						disabled={isDisabled}
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Lock)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Lock) && <Spinner />}
						{t('optimization_groups_status_btn_apply')} {postfix}
					</Button>,
				]
			case OptimizationStatus.Locked:
				return [
					<Button
						key='export'
						size='sm'
						variant='primary'
						disabled={isDisabled}
						onClick={handleOpenExportModal}
					>
						{bulkAction.isPending &&
							actionsInProgress?.has(OptimizationAction.Lock) && <Spinner />}
						{t('optimization_groups_status_btn_export')} {postfix}
					</Button>,
					<Button
						key='unlock'
						size='sm'
						disabled={isDisabled}
						onClick={() =>
							handleOptimizationBulkAction(items, OptimizationAction.Unlock)
						}
					>
						{bulkAction.isPending &&
							actionsInProgress.has(OptimizationAction.Unlock) && <Spinner />}
						{t('optimization_groups_status_btn_unlock')} {postfix}
					</Button>,
				]
			default:
				return null
		}
	}

	return (
		<div className='relative flex items-center'>
			<Item
				size='sm'
				data-active={!expandedStatus && !filteredBySchedule}
				className={statusVariants()}
				tabIndex={0}
				onClick={() => handleSetExpandedStatus()}
			>
				<ItemContent>
					<ItemTitle>
						{totalCount}
						{updatingCount > 0 && (
							<Tag onClick={handleShowOnlyUpdating}>
								{`${updatingCount} updating`}
								<Spinner className='ml-1' />
							</Tag>
						)}
					</ItemTitle>
					<ItemDescription className='text-xs font-medium'>
						{t('general_all')}
					</ItemDescription>
				</ItemContent>
			</Item>
			<Separator orientation='vertical' className='mx-4 h-5!' />
			{statusesFlow.map((key, index: number) => {
				const isNotLastItem = index < statusesFlow.length - 1
				const items = optimizationsByStatus?.[key] ?? []
				const isActive = expandedStatus?.includes(key)
				const itemsToAction =
					selectionCount > 0
						? items.filter(({ group }) => rowSelection[group.id])
						: items

				return (
					<Fragment key={key}>
						<Item
							size='xs'
							data-active={isActive}
							className={statusVariants({ variant: key as any })}
							tabIndex={0}
							onClick={() => handleSetExpandedStatus(key)}
						>
							<ItemContent>
								<ItemTitle>{items?.length ?? 0}</ItemTitle>
								<ItemDescription className='text-xs font-medium'>
									{t(`og_status_${key}`)}
								</ItemDescription>
							</ItemContent>
							{isActive && !!itemsToAction.length && (
								<ItemActions className='flex items-center transition-all'>
									{getActions(key, itemsToAction)}
								</ItemActions>
							)}
						</Item>
						{isNotLastItem && (
							<ChevronRightIcon className='size-4 text-muted-foreground/50' />
						)}
					</Fragment>
				)
			})}
		</div>
	)
}

const statusesFlow: AssociatedStatus[] = [
	OptimizationStatus.New,
	OptimizationStatus.Pending,
	OptimizationStatus.Scheduled,
	OptimizationStatus.Failed,
	OptimizationStatus.Finished,
	OptimizationStatus.Locked,
]
const statusVariants = cva('cursor-pointer transition-all', {
	variants: {
		variant: {
			neutral: 'hover:bg-tertiary data-[active=true]:bg-tertiary ',
			[OptimizationStatus.New]:
				'hover:bg-tertiary data-[active=true]:bg-tertiary',
			[OptimizationStatus.Pending]:
				'hover:bg-orange-100 dark:hover:bg-orange-900 data-[active=true]:bg-orange-100 dark:data-[active=true]:bg-orange-900',
			[OptimizationStatus.Scheduled]:
				'hover:bg-orange-100 dark:hover:bg-orange-900 data-[active=true]:bg-orange-100 dark:data-[active=true]:bg-orange-900',
			[OptimizationStatus.Failed]:
				'hover:bg-red-100 dark:hover:bg-red-900 data-[active=true]:bg-red-100 dark:data-[active=true]:bg-red-900',
			[OptimizationStatus.Finished]:
				'hover:bg-green-100 dark:hover:bg-green-900 data-[active=true]:bg-green-100 dark:data-[active=true]:bg-green-900',
			[OptimizationStatus.Locked]:
				'hover:bg-primary-100 dark:hover:bg-primary-900 data-[active=true]:bg-primary-100 dark:data-[active=true]:bg-primary-900',
		},
	},
	defaultVariants: { variant: 'neutral' },
})

const trackMassRepricingFilter = (status?: string) => {
	switch (status) {
		case OptimizationStatus.New:
			return ampli.massRepricingFilterNew()
		case OptimizationStatus.Scheduled:
			return ampli.massRepricingFilterStarted()
		case OptimizationStatus.Failed:
			return ampli.massRepricingFilterFailed()
		case OptimizationStatus.Finished:
			return ampli.massRepricingFilterFinished()
		case OptimizationStatus.Locked:
			return ampli.massRepricingFilterLocked()
		case OptimizationStatus.Pending:
			return ampli.massRepricingFilterPending()
		default:
			return ampli.massRepricingFilterAll()
	}
}
const trackAnalyticEvent = (status: string, count: number) => {
	const options = {
		'Mass action by': count > 0 ? 'Selected' : 'All',
	}
	switch (status) {
		case OptimizationStatus.New:
			return ampli.massRepricingActionStart(options)
		case OptimizationStatus.Scheduled:
			return ampli.massRepricingActionStop(options)
		case OptimizationStatus.Failed:
			return ampli.massRepricingActionRestart(options)
		case OptimizationStatus.Finished:
			return ampli.massRepricingActionApply(options)
		case OptimizationStatus.Locked:
			return ampli.massRepricingActionRevoke(options)
		case OptimizationStatus.Pending:
			return ampli.massRepricingActionPending(options)
		default:
			return ampli.massRepricingActionStart(options)
	}
}
const hasExportedOptimization = (items: OptimizationGroupTableRow[]) =>
	items.some(({ optimization }) => optimization.is_exported)
const canMakeActionOnList = (
	action: AssociatedStatus,
	items: OptimizationGroupTableRow[]
) => {
	if (OptimizationStatus.Locked === action)
		return items.every(
			({ optimization }) => optimization.status === OptimizationStatus.Locked
		)
	else {
		return items.every(
			({ optimization }) => optimization.status !== OptimizationStatus.Locked
		)
	}
}

const getOptGroupId = ({ group }: OptimizationGroupTableRow) => group.id
const askToApplyOptimization = (t: TFunction) =>
	confirm({
		title: t('optimization_export_again_title'),
		description: t('optimization_export_again_message'),
		actionText: t('general_apply'),
		cancelText: t('general_cancel'),
	})
const askToRemoveExportedPrices = (t: TFunction) =>
	clarify({
		title: t('opt.confirm.revert_applied.title'),
		description: t('opt.confirm.revert_applied.description'),
		actions: [
			{
				actionId: 'keep_optimized_prices',
				ActionProps: { variant: 'secondary' },
				actionText: t(
					'opt.confirm.revert_applied.action.keep_optimized_prices'
				),
			},
			{
				actionId: 'revert_full_state',
				ActionProps: { variant: 'primary' },
				actionText: t('opt.confirm.revert_applied.action.revert_full_state'),
			},
		],
	})
